/**
 * FTR5b: click-to-fly overlay, controls legend, and pointer-lock indicator.
 *
 * The #hud overlay is pointer-events:none; THIS overlay is pointer-events:auto
 * so its CLICK TO FLY surface can receive the click that requests pointer lock.
 * On pointerlockchange we re-show the overlay when lock is lost (Esc), and show
 * a small "MOUSE CAPTURED — ESC TO RELEASE" indicator while locked.
 */
import './startupOverlay.css';

/** The SHORT card hint (startup overlay only). */
const LEGEND_TEXT =
  'W/S throttle · A/D strafe · Space/Ctrl up/down · Q/E roll · ' +
  'MOUSE look · F assist · C cam · TAB target · X supercruise · Z cut throttle · H controls';

/** UX polish: the COMPLETE control reference for the H panel (one per line). */
const LEGEND_FULL: readonly string[] = [
  'W / S — throttle up / down (latched)',
  'Z — cut throttle',
  'A / D — strafe · SPACE / CTRL — up / down',
  'Q / E — roll · ARROWS — yaw / pitch',
  'MOUSE — look (virtual stick)',
  'SHIFT — boost',
  'F — flight assist · V — rot-assist / VERIFY',
  '1 / 2 / 3 — power pips SYS / ENG / WEP',
  'C — chase / cockpit camera',
  'TAB — target menu · N — nearest signal',
  'X — supercruise / drop',
  'G — dock / undock · R — fire · T — scoop',
  'O — orbit lines · ENTER — comms',
  'H — toggle this panel (drag to move)',
];

/** localStorage key for the panel's dragged position. */
const LEGEND_POS_KEY = 'blackmarble.ui.legendPos';

export class StartupOverlay {
  private readonly overlay: HTMLDivElement;
  private readonly legendLine: HTMLDivElement;
  private readonly legend: HTMLDivElement;
  private readonly capture: HTMLDivElement;
  // UX polish: the H panel is OFF by default (toggle on demand, never always-on).
  private legendVisible = false;
  private wasLocked = false; // M1.1c: only re-show overlay after a HELD lock is lost
  private flightStarted = false; // COLLISION-1: once flying, never re-show on lock-loss
  private dragOffset: { x: number; y: number } | null = null;
  // ITEM 3 ADJUST: timer that fades the centred capture hint out ~2s after lock.
  private captureTimer: number | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.overlay = document.createElement('div');
    this.overlay.id = 'startup-overlay';
    this.overlay.innerHTML = `
      <div class="so-card hud-glow">
        <div class="so-title">CLICK TO FLY</div>
        <div class="so-sub">MOUSE LOOK · KEYBOARD THRUST</div>
        <div class="so-legend">${LEGEND_TEXT}</div>
      </div>`;
    document.body.appendChild(this.overlay);
    this.legendLine = this.overlay.querySelector('.so-legend') as HTMLDivElement;

    // UX polish: the H panel — complete reference, hidden by default, draggable
    // with a localStorage-sticky position, non-intrusive default (right edge,
    // below the ship panel, above the radar), z-ordered UNDER every menu.
    this.legend = document.createElement('div');
    this.legend.id = 'controls-legend';
    this.legend.innerHTML =
      `<div class="cl-head">CONTROLS</div>` +
      LEGEND_FULL.map((l) => `<div class="cl-row">${l}</div>`).join('');
    this.legend.style.display = 'none';
    document.body.appendChild(this.legend);
    this.restoreLegendPos();
    this.bindLegendDrag();

    this.capture = document.createElement('div');
    this.capture.id = 'capture-indicator';
    this.capture.className = 'hud-glow';
    this.capture.textContent = 'MOUSE CAPTURED — ESC TO RELEASE';
    this.capture.style.display = 'none';
    document.body.appendChild(this.capture);

    // M1.1c: a single click ALWAYS dismisses the overlay and starts flight
    // (drag-look + keyboard work without pointer-lock). The lock request is a
    // guarded nice-to-have whose sync throw (WrongDocumentError) must NOT keep
    // the overlay up. We only re-show the overlay if lock was ACTUALLY held and
    // is then lost (Esc) — never on a spurious denial.
    this.overlay.addEventListener('click', () => {
      this.dismiss();
      try {
        const p = this.canvas.requestPointerLock();
        if (p && typeof (p as Promise<void>).catch === 'function') {
          (p as Promise<void>).catch(() => undefined);
        }
      } catch {
        /* pointer-lock unavailable — drag + keyboard flight still work */
      }
    });

    document.addEventListener('pointerlockchange', () => {
      const locked = document.pointerLockElement === this.canvas;
      if (locked) {
        this.wasLocked = true;
        this.overlay.style.display = 'none';
        this.showCaptureHint();
      } else {
        this.hideCaptureHint();
        // COLLISION-1: once flight has started, NEVER re-show the overlay just
        // because pointer-lock dropped (it drops during normal maneuvering).
        // Drag-look + arrow-look keep working without lock, so the player is
        // never trapped behind the overlay mid-flight.
        if (this.wasLocked && !this.flightStarted) this.overlay.style.display = 'flex';
        this.wasLocked = false;
      }
    });

    document.addEventListener('pointerlockerror', () => {
      this.hideCaptureHint(); // overlay already dismissed; keep playing
    });
  }

  /** ITEM 3 ADJUST: show the centred capture hint, then fade it out after ~2s.
   * Re-locking clears any pending fade and replays the cue (opacity 1 → 0). */
  private showCaptureHint(): void {
    if (this.captureTimer !== null) {
      window.clearTimeout(this.captureTimer);
      this.captureTimer = null;
    }
    this.capture.style.display = 'block';
    void this.capture.offsetWidth; // reflow so the opacity transition replays
    this.capture.style.opacity = '1';
    this.captureTimer = window.setTimeout(() => {
      this.capture.style.opacity = '0'; // CSS transition handles the ~400ms fade
      this.captureTimer = null;
    }, 2000);
  }

  /** ITEM 3 ADJUST: hide the capture hint immediately (lock lost / error). */
  private hideCaptureHint(): void {
    if (this.captureTimer !== null) {
      window.clearTimeout(this.captureTimer);
      this.captureTimer = null;
    }
    this.capture.style.opacity = '0';
    this.capture.style.display = 'none';
  }

  /** Hide the overlay immediately and mark flight started (idempotent). */
  private dismiss(): void {
    this.overlay.style.display = 'none';
    this.flightStarted = true; // COLLISION-1: lock-loss no longer re-shows it
  }

  /** CAMPAIGN W1: hide CLICK TO FLY while the cold open plays (pre-flight). */
  suppress(): void {
    this.overlay.style.display = 'none';
  }

  /** CAMPAIGN W1: re-show CLICK TO FLY after the cold open resolves. */
  release(): void {
    if (!this.flightStarted) this.overlay.style.display = 'flex';
  }

  get isVisible(): boolean {
    return this.overlay.style.display !== 'none';
  }

  /** Called from the input edge flag (H) to toggle the controls panel. */
  toggleLegend(): void {
    this.legendVisible = !this.legendVisible;
    this.legend.style.display = this.legendVisible ? 'block' : 'none';
  }

  setHintsEnabled(enabled: boolean): void {
    this.legendLine.style.display = enabled ? 'block' : 'none';
    if (!enabled) {
      this.legendVisible = false;
      this.legend.style.display = 'none';
    }
  }

  /** UX polish: restore the dragged position (clamped on-screen), if saved. */
  private restoreLegendPos(): void {
    try {
      const raw = localStorage.getItem(LEGEND_POS_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as { x: number; y: number };
      if (typeof p.x !== 'number' || typeof p.y !== 'number') return;
      const x = Math.max(0, Math.min(window.innerWidth - 80, p.x));
      const y = Math.max(0, Math.min(window.innerHeight - 60, p.y));
      this.legend.style.left = `${x}px`;
      this.legend.style.top = `${y}px`;
      this.legend.style.right = 'auto';
    } catch {
      /* corrupt position — keep the CSS default */
    }
  }

  /** UX polish: drag-to-move; position persists across sessions. */
  private bindLegendDrag(): void {
    this.legend.addEventListener('mousedown', (e) => {
      const r = this.legend.getBoundingClientRect();
      this.dragOffset = { x: e.clientX - r.left, y: e.clientY - r.top };
      e.stopPropagation(); // never starts a flight drag-look
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.dragOffset) return;
      const x = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - this.dragOffset.x));
      const y = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - this.dragOffset.y));
      this.legend.style.left = `${x}px`;
      this.legend.style.top = `${y}px`;
      this.legend.style.right = 'auto';
    });
    window.addEventListener('mouseup', () => {
      if (!this.dragOffset) return;
      this.dragOffset = null;
      const r = this.legend.getBoundingClientRect();
      try {
        localStorage.setItem(LEGEND_POS_KEY, JSON.stringify({ x: r.left, y: r.top }));
      } catch {
        /* storage unavailable — position just won't stick */
      }
    });
  }
}
